import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import PaymentMethodValidation from './PaymentMethodValidation';
import PaymentMethod from './PaymentMethod';

import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@ontruck/ontruck-react-components/lib/Modal';
import Input from '@ontruck/ontruck-react-components/lib/Input';
import Button from '@ontruck/ontruck-react-components/lib/Button';
import Checkbox from '@ontruck/ontruck-react-components/lib/Checkbox';

import {
  Section,
  SectionTitle,
  Row,
  Column,
  // BlockSection
} from 'components/ModalFormLayout';

class ShipperEditModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    shipper: PropTypes.object,
    onClose: PropTypes.func,
    intl: PropTypes.any,
    onSave: PropTypes.func,
    onSaveSuccesfully: PropTypes.func,
    shippersMinimalisticForms: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    const { shipper } = props;

    this.state = {
      shipper,
      fieldErrors: [],
    };

    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.showFieldError = this.showFieldError.bind(this);
    this.handleServerSaveError = this.handleServerSaveError.bind(this);
    this.clearErrors = this.clearErrors.bind(this);
    this.validate = this.validate.bind(this);
  }

  showFieldError(field) {
    const { fieldErrors } = this.state;

    return fieldErrors.indexOf(field) !== -1;
  }

  clearErrors() {
    this.setState({
      fieldErrors: [],
    });
  }

  validate() {
    const { shipper = {} } = this.state;

    const validations = {
      company_name: (value) => value && value !== '',
      phone: (value) => /^\+?[0-9]\d{1,14}$/.test(value),
    };

    return new Promise((resolve, reject) => {
      const fieldErrors = Object.keys(validations)
        .map((field) => (validations[field](shipper[field]) ? null : field))
        .filter((error) => error);

      if (fieldErrors.length === 0) {
        resolve();
      } else {
        this.setState(
          {
            fieldErrors,
          },
          () => reject(fieldErrors)
        );
      }
    });
  }

  handleServerSaveError() {
    // TODO parse server error

    const { fieldErrors } = this.state;

    this.setState({
      fieldErrors: [...fieldErrors],
    });
  }

  handleSave() {
    const { onSave, onSaveSuccesfully } = this.props;

    if (!onSave) {
      return;
    }

    let { shipper = {} } = this.state;

    this.clearErrors();

    this.validate()
      .then(() => {
        // FIXME: this should be done in BE, and be saved with spaces
        if (shipper.fiscal_address && shipper.fiscal_address.zipcode) {
          shipper.fiscal_address.zipcode = shipper.fiscal_address.zipcode.replace(' ', '');
        }

        onSave(shipper.id, shipper)
          .then((valueUpdated) => onSaveSuccesfully(valueUpdated))
          .catch(this.handleServerSaveError);
      })
      .catch((error) => console.error(error));
  }

  handleFieldChange(field, clearErrors = true) {
    return (
      value,
      cb = () => {
        /*noop*/
      }
    ) => {
      const { shipper, fieldErrors } = this.state;

      this.setState(
        {
          shipper: {
            ...shipper,
            [field]: value,
          },
          fieldErrors: clearErrors ? fieldErrors.filter((error) => error !== field) : fieldErrors,
        },
        typeof cb === 'function'
          ? cb
          : () => {
              /*noop*/
            }
      );
    };
  }

  handleSubfieldChange(subfield, field) {
    return (value) => {
      const { shipper } = this.state;

      this.setState({
        shipper: {
          ...shipper,
          [subfield]: {
            ...shipper[subfield],
            [field]: value,
          },
        },
      });
    };
  }

  render() {
    const { show, onClose, intl, shippersMinimalisticForms } = this.props;

    const { shipper = {} } = this.state;

    const t = intl.messages.components.ShipperEditModal;

    return (
      <div>
        <Modal show={show} size="L" onClose={onClose} visibleOverflow>
          <ModalHeader>{t.titles.modal}</ModalHeader>
          <ModalBody noPadding light={true}>
            <Section>
              <Row>
                <Column>
                  <SectionTitle>{t.titles.details}</SectionTitle>
                  <Input
                    label={t.labels.company_name}
                    value={shipper.company_name}
                    onChange={this.handleFieldChange('company_name')}
                    error={this.showFieldError('company_name') ? t.errors.company_name : ''}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <Input
                    label={t.labels.phone}
                    value={shipper.phone}
                    onChange={this.handleFieldChange('phone')}
                    error={this.showFieldError('phone') ? t.errors.phone : ''}
                  />
                </Column>
              </Row>
            </Section>

            {!shippersMinimalisticForms && (
              <Section>
                <Row>
                  <Column>
                    <SectionTitle>{t.titles.special_requeriments}</SectionTitle>
                    <Input
                      label={t.titles.special_requeriments_link}
                      value={shipper.special_requirements}
                      onChange={this.handleFieldChange('special_requirements')}
                      error={this.showFieldError('special_requirements') ? 'error' : ''}
                    />
                  </Column>
                </Row>
              </Section>
            )}

            <Section>
              <Row>
                <Column>
                  <SectionTitle>{t.titles.fiscal_address}</SectionTitle>
                  <Input
                    label={t.labels.fiscal_address_address_line}
                    value={shipper.fiscal_address && shipper.fiscal_address.address_line}
                    onChange={this.handleSubfieldChange('fiscal_address', 'address_line')}
                  />
                </Column>
              </Row>
              <Row>
                <Column third>
                  <Input
                    label={t.labels.fiscal_address_zipcode}
                    value={shipper.fiscal_address && shipper.fiscal_address.zipcode}
                    onChange={this.handleSubfieldChange('fiscal_address', 'zipcode')}
                  />
                </Column>
                <Column>
                  <Input
                    label={t.labels.fiscal_address_city}
                    value={shipper.fiscal_address && shipper.fiscal_address.city}
                    onChange={this.handleSubfieldChange('fiscal_address', 'city')}
                  />
                </Column>
              </Row>
            </Section>

            <Section>
              <SectionTitle>{t.titles.payment}</SectionTitle>
              <Row>
                <Column third>
                  <Input
                    label={t.labels.tax_number}
                    value={shipper.tax_number}
                    onChange={this.handleFieldChange('tax_number')}
                  />
                </Column>
                <Column />
              </Row>
              <Row>
                <Column twoThirds>
                  <PaymentMethod
                    value={shipper.payment_method}
                    handleChange={this.handleFieldChange('payment_method')}
                    t={t}
                  />
                </Column>
              </Row>

              <Row>
                <Column twoThirds>
                  <PaymentMethodValidation
                    value={shipper.payment_method_validation_status}
                    handleChange={this.handleFieldChange('payment_method_validation_status')}
                    t={t}
                  />
                </Column>
              </Row>

              {shipper.payment_method === 'TRANSFER' ? (
                <Row>
                  <Column twoThirds>
                    <Input
                      label={t.labels.bank_account}
                      value={shipper.bank_account}
                      onChange={this.handleFieldChange('bank_account')}
                    />
                  </Column>
                </Row>
              ) : null}
              <Row>
                {!shippersMinimalisticForms && (
                  <Column>
                    <Checkbox
                      label={t.labels.manual_billing}
                      checked={shipper.manual_billing}
                      onChange={this.handleFieldChange('manual_billing')}
                    />
                  </Column>
                )}
                {!shippersMinimalisticForms && (
                  <Column>
                    <Checkbox
                      label={t.labels.pending_payments}
                      checked={shipper.pending_payments}
                      onChange={this.handleFieldChange('pending_payments')}
                    />
                  </Column>
                )}
                {!shippersMinimalisticForms && (
                  <Column>
                    <Checkbox
                      label={t.labels.show_special_review_features}
                      checked={shipper.show_special_review_features}
                      onChange={this.handleFieldChange('show_special_review_features')}
                    />
                  </Column>
                )}
              </Row>
            </Section>
            <Section>
              <SectionTitle>{t.titles.shipper_status}</SectionTitle>
              <Row>
                <Column twoThirds>
                  <Checkbox
                    label={t.labels.is_hidden_on_search}
                    checked={shipper.is_hidden_on_search}
                    onChange={this.handleFieldChange('is_hidden_on_search')}
                  />
                  <p>{t.labels.is_hidden_on_search_info}</p>
                </Column>
              </Row>
            </Section>

            {/*<BlockSection
            title={ t.titles.block }
            blocked={shipper.status === 'BLOCKED'}
            onChange={(blocked) => this.handleFieldChange('status')(blocked ? 'BLOCKED' : '')}
            helpText={ t.block_help }
            switchText={ shipper.status === 'BLOCKED' ? t.block_blocked : t.block_block }
          />*/}
          </ModalBody>
          <ModalFooter light>
            <Button fullWidth onClick={this.handleSave}>
              {t.save}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default injectIntl(ShipperEditModal);
